import isEmpty from 'lodash/isEmpty'
import React, { useEffect } from 'react'
import { TabularTree, type TabularTreePrimaryRowProps } from '@ztechapps/webui.data-tools'
import { Alert, HorizontalStack, LoadingPlaceholder, Scrollable, Switch, TooltipPopOver } from '@ztechapps/webui.util'
import type { ProductWidgetProps } from '@ztechapps/webui.contract'
import { useInjectComponent, WebuiIocKey } from '@ztechapps/webui.contract'
import { type EntityWithSelection } from '@ztechapps/common.hook'
import type { Product, ProductBillOfMaterial } from '@ztechapps/common.contract'
import { useCreateAndBuildContext } from './Context'
import { formatNumber, groupTree, mapTree } from '@ztechapps/common.utils'
import { CheckboxInput } from '@ztechapps/webui.input'
import get from 'lodash/get'
import set from 'lodash/set'
import cloneDeep from 'lodash/cloneDeep'

const CHILDREN_NAME = 'product.billOfMaterials'

type TreeNode = Record<string, any>

function mapTreeBottomUp<T extends TreeNode>(
  root: T,
  mapper: (node: T, children: T[]) => T,
  options?: { childrenName?: string }
): T {
  const childrenName = options?.childrenName ?? 'items'

  const traverse = (node: T): T => {
    const children = get(node, childrenName, []) as T[]

    const mappedChildren = children.map(traverse)

    const nodeWithMappedChildren = set(cloneDeep(node), childrenName, mappedChildren)

    return mapper(nodeWithMappedChildren as T, mappedChildren)
  }

  return traverse(cloneDeep(root))
}

export const BillOfMaterial = ({ onChange }) => {
  const { product, loading, hasRequiredSubcomponents } = useCreateAndBuildContext()

  const list = product?.billOfMaterials

  const [hasAllSelected, setHasAllSelected] = React.useState(false)

  const [rootBom, setRootBom] = React.useState<EntityWithSelection<ProductBillOfMaterial>>({
    product: {
      ...product,
      billOfMaterials: list,
    },
    _selected: true,
    requiredToBuild: true,
  } as EntityWithSelection<ProductBillOfMaterial>)

  useEffect(() => {
    //we need to traverse teh list to make sure that bom that is isReuired is always selected
    const rootBom = {
      product: {
        ...product,
        billOfMaterials: list,
      },
      _selected: true,
      // requiredToBuild: true,
    } as EntityWithSelection<ProductBillOfMaterial>
    const productWithNewList = mapTree<EntityWithSelection<ProductBillOfMaterial>>(
      rootBom,
      (node, level, index) => {
        //if the node is required in billOfmaterials, then we need to set it as selected as the default
        return {
          ...node,
          _id: `${node?._id}_${level}_${index}`,
          _selected: node.requiredToBuild ? true : false,
        }
      },
      { childrenName: CHILDREN_NAME }
    )

    setRootBom(productWithNewList)
    setHasAllSelected(false) //reset the selection
  }, [product, list])

  //Everytime the rootBom changes, we need to trigger the onChange
  useEffect(() => {
    onChange(rootBom?.product?.billOfMaterials ?? [])
    // `onChange` is deliberately excluded — this is a Formik `Field` bound with a bare `onChange`
    // boolean prop (Layout.tsx), which `@ztechapps/webui.form`'s `Field` resolves to a brand-new
    // `(e) => form.setFieldValue(name, e)` closure on every render (libs/webui/form/src/Form/Field.tsx).
    // Depending on it would call setFieldValue every render, which re-renders the form, which
    // creates a new `onChange`, which re-fires this effect — an infinite loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rootBom])

  const handleToggleAll = () => {
    //group by product _id, and node._selected and node.requiredToBuild
    const grouped = groupTree(
      rootBom,
      (node) => `${node?.product?._id}-${node?.requiredToBuild}-${node?._selected}`,
      (groupId, nodes) => {
        return {
          groupId,
          _selected: nodes?.[0]?._selected,
          requiredToBuild: nodes?.[0]?.requiredToBuild,
          product: {
            _id: nodes?.[0]?.product._id,
            code: nodes?.[0]?.product?.code,
          },
        }
      },
      { childrenName: CHILDREN_NAME, startsFromRoot: false }
    )

    //convert to array
    const groupedArray = Object.values(grouped).filter((m) => m._selected && !m.requiredToBuild)

    //if !isEmpty(hasSelectedForNonRequired.product.billOfMaterials), then we need to set hasAllSelected to false
    const hasSelectedForNonRequired = !isEmpty(groupedArray)

    //we just invert the value
    setHasAllSelected(!hasSelectedForNonRequired)

    //now that we figured out that hasSelectedForNonRequired value, we need to map again the rootBom and set the _selected
    // !hasSelectedForNonRequired (the inverse of it)

    const newList = toggleAll(!hasSelectedForNonRequired, rootBom)
    setRootBom(newList)
  }

  function toggleAll(selected: boolean, _bom: EntityWithSelection<ProductBillOfMaterial>) {
    const newList = mapTree(
      rootBom,
      (node) => {
        return {
          ...node,
          _selected: node.requiredToBuild ? true : selected,
        }
      },
      { childrenName: CHILDREN_NAME }
    )

    return newList
  }

  const handleToggleById = (
    item: EntityWithSelection<ProductBillOfMaterial>,
    _parent: EntityWithSelection<ProductBillOfMaterial>
  ) => {
    const toggledValue = item.requiredToBuild ? true : !item._selected

    // Pass 1: Toggle subtree
    const toggled = mapTree(
      rootBom,
      (node, level, index, parentNode) => {
        const isToggledNode = node._id === item._id
        const parentInSubtree =
          parentNode &&
          get(parentNode, '_selected') === toggledValue &&
          (parentNode._id === item._id || get(parentNode, '_inSubtree'))

        const inSubtree = isToggledNode || parentInSubtree

        if (inSubtree) {
          const newNode = { ...node }
          newNode._selected = node.requiredToBuild ? true : toggledValue
          newNode['_inSubtree'] = true
          return newNode
        }

        return node
      },
      { childrenName: CHILDREN_NAME }
    )

    // Pass 2: Bottom-up upward selection propagation
    const withParentSelectionFixed = mapTreeBottomUp(
      toggled,
      (node, children) => {
        if (isEmpty(children)) return node

        const anySelected = children.some((child) => get(child, '_selected'))
        const allUnselected = children.every((child) => !get(child, '_selected'))

        const newNode = { ...node }
        newNode._selected = node.requiredToBuild
          ? true
          : anySelected
            ? true
            : allUnselected
              ? false
              : get(node, '_selected')
        return newNode
      },
      { childrenName: CHILDREN_NAME }
    )

    // Pass 3: Clean up _inSubtree
    const cleaned = mapTree(
      withParentSelectionFixed,
      (node) => {
        const { _inSubtree, ...rest } = node as any
        return rest
      },
      { childrenName: CHILDREN_NAME }
    )

    setRootBom(cleaned)
  }

  return (
    <LoadingPlaceholder loading={loading}>
      <Scrollable height="50vh">
        <Switch expression={hasRequiredSubcomponents}>
          {{
            true: (
              <TabularTree<EntityWithSelection<ProductBillOfMaterial>>
                root={rootBom}
                head={<TabularTreeHeader handleToggleAll={handleToggleAll} hasAllSelected={hasAllSelected} />}
                row={(props) => <TabularTreeRow {...props} handleToggleById={handleToggleById} />}
                childrenKey="product.billOfMaterials"
              />
            ),
            false: (
              <Alert>Cannot do Production for products without required sub-assembly; do Adjustment instead.</Alert>
            ),
          }}
        </Switch>
      </Scrollable>
    </LoadingPlaceholder>
  )
}

export const TabularTreeHeader = ({
  handleToggleAll,
  hasAllSelected,
}: {
  handleToggleAll: () => void
  hasAllSelected: boolean
}) => {
  return (
    <thead>
      <tr className="table-secondary sticky-top">
        <th>
          <HorizontalStack gap={4}>
            <div>Product</div>
          </HorizontalStack>
        </th>

        <TooltipPopOver tooltipBody="This is the number of units required to build the single unit of final product">
          <th style={{ width: '8%' }} className={'text-end'}>
            Qty. required
          </th>
        </TooltipPopOver>

        <TooltipPopOver tooltipBody="The number of units needed to build the parent product">
          <th style={{ width: '8%' }} className={'text-end'}>
            Needed
          </th>
        </TooltipPopOver>
        <TooltipPopOver tooltipBody="The number of units that will be consumed (from the current inventory) to build the parent product">
          <th style={{ width: '8%' }} className={'text-end'}>
            Consumed
          </th>
        </TooltipPopOver>
        <TooltipPopOver tooltipBody="The number of units that is left after the consumption.">
          <th style={{ width: '8%' }} className={'text-end'}>
            Remaining
          </th>
        </TooltipPopOver>
        <TooltipPopOver tooltipBody="Include or exclude this product for production.">
          <th style={{ width: '8%' }} className={'text-center'}>
            <CheckboxInput onChange={handleToggleAll} checked={hasAllSelected} />
          </th>
        </TooltipPopOver>
      </tr>
    </thead>
  )
}

export const TabularTreeRow = ({
  leftMarginStyle,
  item,
  children,
  handleToggleById,
  parent,
}: {
  handleToggleById: (
    item: EntityWithSelection<ProductBillOfMaterial>,
    parent: EntityWithSelection<ProductBillOfMaterial>
  ) => void
} & TabularTreePrimaryRowProps<EntityWithSelection<ProductBillOfMaterial>>) => {
  const ProductWidget = useInjectComponent<ProductWidgetProps>(WebuiIocKey.product.productWidget)
  const bomProduct = item?.product as Product

  const neededMoreUnit = bomProduct.requiredComputedQuantityOnHand > 0

  const rowBg = () => {
    const { requiredToBuild, _selected } = item

    if (requiredToBuild) {
      return neededMoreUnit ? 'table-danger' : ''
    }

    return neededMoreUnit && _selected ? 'table-danger' : 'table-warning'
  }

  const disabledCheckbox = item.requiredToBuild || parent?.product?.requiredComputedQuantityOnHand <= 0

  return (
    <tr className={rowBg()}>
      <td>
        <HorizontalStack gap={1} style={leftMarginStyle}>
          {children}
          <ProductWidget {...bomProduct} />
        </HorizontalStack>
      </td>

      <td className={'text-end'}>{formatNumber(item.quantity)}</td>

      <td className={'text-end'}>{formatNumber(bomProduct.requiredComputedQuantityOnHand)}</td>
      <td className={'text-end'}>{formatNumber(item?.sharedInventory)}</td>
      <td className={'text-end'}>{formatNumber(item?.runningInventory)}</td>
      <TooltipPopOver
        tooltipBody={
          disabledCheckbox && !item.requiredToBuild
            ? `Cannot check/uncheck becuase '${parent?.product?.code}' is not needed to build.`
            : "Cannot check/uncheck becuase it's required to build."
        }
      >
        <td className={'text-center'}>
          <CheckboxInput
            checked={item._selected}
            disabled={disabledCheckbox}
            onChange={() => handleToggleById(item, parent)}
          />
        </td>
      </TooltipPopOver>
    </tr>
  )
}
